// The single offering close instant (owner decision 2026-10-20).
//
// The closing date an issuer chooses means the END OF THAT DAY IN EASTERN
// TIME: 11:59:59 PM America/New_York, daylight-saving aware. This mirrors
// app/services/offering_close.py (offering_close_instant) so every display
// names the moment the server enforces.
//
// A stored close value (home.funding_round_end, a track's funding_round_end)
// arrives as a naive "YYYY-MM-DDTHH:MM:SS" string:
// - exactly midnight is a chosen calendar date: the close is 11:59:59 PM ET
//   that day (owner rule);
// - any other value is an exact UTC instant (the "close now" writers).
// A string with "Z" or an offset is already an instant (for example the
// investor page's closes_at) and is used as given.

const TIME_ZONE = "America/New_York";
const HOUR_MS = 60 * 60 * 1000;
const NAIVE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/;
const HAS_ZONE = /(?:[zZ]|[+-]\d\d:?\d\d)$/;

// AP style month abbreviations (March to July are spelled out).
const AP_MONTHS = [
  "Jan.", "Feb.", "March", "April", "May", "June",
  "July", "Aug.", "Sept.", "Oct.", "Nov.", "Dec.",
];

const PARTS = new Intl.DateTimeFormat("en-US", {
  timeZone: TIME_ZONE,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

function easternParts(date) {
  const parts = {};
  PARTS.formatToParts(date).forEach((p) => { parts[p.type] = p.value; });
  return {
    year: +parts.year,
    month: +parts.month,
    day: +parts.day,
    hour: +parts.hour % 24,
    minute: +parts.minute,
    second: +parts.second,
  };
}

function easternWallClockAsUtc(instantMs) {
  const p = easternParts(new Date(instantMs));
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

// An Eastern wall-clock time as a UTC instant (ms).
export function easternWallClockToInstant(year, monthIndex, day, hour = 0, minute = 0, second = 0) {
  const wall = Date.UTC(year, monthIndex, day, hour, minute, second);
  let guess = wall + 5 * HOUR_MS;
  for (let i = 0; i < 3; i += 1) {
    guess = wall - (easternWallClockAsUtc(guess) - guess);
  }
  return guess;
}

// 11:59:59 PM Eastern on the given calendar day, as a UTC instant (ms).
export function easternEndOfDay(year, monthIndex, day) {
  return easternWallClockToInstant(year, monthIndex, day, 23, 59, 59);
}

// The close instant (a Date) for a stored close value, or null.
export function offeringCloseInstant(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const text = String(value).trim();
  if (HAS_ZONE.test(text)) {
    const parsed = new Date(text);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const m = NAIVE.exec(text);
  if (!m) return null;
  const [year, month, day] = [+m[1], +m[2] - 1, +m[3]];
  const [hour, minute, second] = [+(m[4] || 0), +(m[5] || 0), +(m[6] || 0)];
  const fraction = /\.(\d+)$/.exec(text);
  const hasFraction = Boolean(fraction && Number(fraction[1]) !== 0);
  if (hour === 0 && minute === 0 && second === 0 && !hasFraction) {
    return new Date(easternEndOfDay(year, month, day));
  }
  const parsed = new Date(`${text.replace(" ", "T")}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// True once the close instant has passed (no close value: false).
export function offeringClosed(value, now = new Date()) {
  const close = offeringCloseInstant(value);
  return Boolean(close) && now.getTime() >= close.getTime();
}

// "Jan. 12, 2027" for an instant (its Eastern calendar date).
export function formatApDateEt(instant) {
  if (!instant) return "";
  const p = easternParts(instant);
  return `${AP_MONTHS[p.month - 1]} ${p.day}, ${p.year}`;
}

// "Jan. 12, 2027, 11:59 PM ET" for an instant.
export function formatInstantEt(instant) {
  if (!instant) return "";
  const p = easternParts(instant);
  const hour = p.hour % 12 || 12;
  const meridiem = p.hour < 12 ? "AM" : "PM";
  const minute = String(p.minute).padStart(2, "0");
  return `${formatApDateEt(instant)}, ${hour}:${minute} ${meridiem} ET`;
}

// "Jan. 12, 2027, 11:59 PM ET" for a stored close value ("" without one).
export function formatOfferingCloseEt(value) {
  return formatInstantEt(offeringCloseInstant(value));
}

// "Jan. 12, 2027" (the Eastern date of the close) for a stored close value.
export function formatOfferingCloseDateEt(value) {
  return formatApDateEt(offeringCloseInstant(value));
}
